import { useState } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
  flexRender,
} from "@tanstack/react-table";
import dayjs from "dayjs";
import "dayjs/locale/id";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { getAuthUser } from "../../services/auth";
import "./Vendor.css";
import Swal from "sweetalert2";

dayjs.locale("id");

export default function VendorTable({
  vendors,
  onEdit,
  onDelete,
  tableLoading,
  page,
  perPageSize,
  search,
}) {
  const columnHelper = createColumnHelper();
  const auth = getAuthUser();

  const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const highlightText = (text, keyword) => {
    if (!keyword) return text;

    keyword = keyword.replace(/[^\p{L}\p{N}]+/gu, " ");
    // Ambil hanya kata alphanumeric, abaikan simbol asing
    const keywords = Array.from(
      new Set(keyword.trim().split(/\s+/).filter(Boolean))
    ).map(escapeRegex);

    if (keywords.length === 0) return text;

    const regex = new RegExp(`(${keywords.join("|")})`, "gi");

    const parts = String(text).split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <span key={i} className="highlight-vendor-table">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const columns = [
    columnHelper.display({
      id: "no",
      header: "No",
      size: 50,
      cell: ({ row, table }) => {
        const pageIndex = table.options.meta?.page || 1;
        const perPageSize = table.options.meta?.perPageSize || 10;
        const no = row.index + 1 + (pageIndex - 1) * perPageSize;
        return <div className="text-center">{no}</div>;
      },
    }),
    columnHelper.accessor((row) => row.company_name, {
      header: "Nama Perusahaan",
      cell: (info) => {
        const value = info.getValue() || "-";
        return (
          <div
            className="text-wrap text-break"
            style={{ minWidth: "250px", maxWidth: "300px" }}
          >
            {highlightText(value, search)}
          </div>
        );
      },
    }),
    columnHelper.accessor("branch", {
      header: "Cabang",
      cell: (info) => {
        const value = info.getValue() || "-";
        return (
          <div className="text-wrap text-break" style={{ maxWidth: "200px" }}>
            {highlightText(value, search)}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.brand, {
      header: "Merk",
      cell: (info) => {
        const value = info.getValue() || "-";
        return (
          <div className="text-wrap text-break" style={{ maxWidth: "200px" }}>
            {highlightText(value, search)}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.sales_name, {
      header: "Nama Sales",
      cell: (info) => {
        const value = info.getValue() || "-";
        return (
          <div className="text-wrap text-break" style={{ maxWidth: "200px" }}>
            {highlightText(value, search)}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.position, {
      header: "Jabatan",
      cell: (info) => {
        const value = info.getValue() || "-";
        return (
          <div
            className="text-wrap text-break"
            style={{ minWidth: "250px", maxWidth: "300px" }}
          >
            {highlightText(value, search)}
          </div>
        );
      },
    }),
    columnHelper.accessor("phone", {
      header: "No HP / Telp Kantor",
      size: 200,
      cell: ({ row }) => {
        const encodedPhone = row.original.phone;
        const countryCode = row.original.country_code;

        if (!encodedPhone || encodedPhone.trim() === "") {
          return (
            <div className="d-flex justify-content-center align-items-center text-muted">
              -
            </div>
          );
        }

        const handleClick = (e) => {
          e.preventDefault();

          let decodedPhone = "";
          try {
            decodedPhone = atob(encodedPhone);
          } catch (err) {
            console.error("Gagal decode:", err);
            alert("Nomor tidak valid");
            return;
          }

          const cleanPhone = decodedPhone.replace(/\D/g, "").replace(/^0+/, "");
          const fullPhone = `${countryCode}${cleanPhone}`;

          if (countryCode) {
            const waLink = `https://wa.me/${
              fullPhone.startsWith("0") ? "62" + fullPhone.slice(1) : fullPhone
            }`;
            window.open(waLink, "_blank", "noopener,noreferrer");
          } else {
            navigator.clipboard.writeText(decodedPhone);
            Swal.fire({
              title: `Nomor telpon rumah disalin: ${decodedPhone}`,
              icon: "success",
              draggable: true,
            });
          }
        };

        return (
          <button
            onClick={handleClick}
            className="btn btn-link text-success d-flex align-items-center justify-content-center gap-1 text-decoration-none"
          >
            {countryCode ? (
              <>
                <i className="fa-brands fa-whatsapp fs-3"></i>
                <span>Whatsapp</span>
              </>
            ) : (
              <>
                <i className="fa-solid fa-phone fs-3"></i>
                <span>Telp Rumah</span>
              </>
            )}
          </button>
        );
      },
    }),
    columnHelper.accessor((row) => row.email, {
      header: "Email",
      cell: (info) => {
        const value = info.getValue() || "-";
        return (
          <div
            className="text-wrap text-break"
            style={{ minWidth: "250px", maxWidth: "300px" }}
          >
            {highlightText(value, search)}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.product_type, {
      header: "Jenis Barang",
      cell: (info) => {
        const value = info.getValue() || "-";
        const items = value
          .split("+")
          .map((value) => value.trim())
          .filter(Boolean);
        const colors = [
          "#0046b0",
          "#9e7700",
          "#a61f2d",
          "#156b43",
          "#0994b0",
          "#43494f",
        ];

        return (
          <div
            className="d-flex flex-wrap gap-2"
            style={{ minWidth: "250px", maxWidth: "300px" }}
          >
            {items.length > 0 ? (
              items.map((item, idx) => (
                <span
                  key={idx}
                  className={"badge text-white"}
                  style={{ backgroundColor: colors[idx % colors.length] }}
                >
                  {highlightText(item, search)}
                </span>
              ))
            ) : (
              <span>-</span>
            )}
          </div>
        );
      },
    }),

    auth?.role == "admin" &&
      columnHelper.display({
        id: "actions",
        header: "Aksi",
        cell: ({ row }) => {
          const item = row.original;
          return (
            <div className="btn-action-vendor d-flex gap-1 justify-content-center flex-md-row flex-column">
              <button
                className="btn btn-sm btn-warning"
                onClick={() => onEdit(item)}
              >
                Edit
              </button>
              <button
                className="btn btn-sm btn-danger"
                onClick={() => onDelete(item.id)}
              >
                Hapus
              </button>
            </div>
          );
        },
        meta: {
          isSticky: true,
          className: "size-column-vendor",
        },
      }),
  ].filter(Boolean);

  const table = useReactTable({
    data: vendors,
    columns,
    meta: {
      page,
      perPageSize,
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: Math.ceil(vendors.length / 10),
  });

  return (
    <>
      {tableLoading ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-success" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div style={{ maxHeight: "543px", overflowY: "auto" }}>
          <table className="table table-bordered table-striped">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={header.column.columnDef.meta?.className || ""}
                      style={{
                        width: header.getSize(),
                        textAlign: "center",
                        position: "sticky",
                        top: 0,
                        background: "white",
                        zIndex: 2,
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows?.length > 0 ? (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        style={{
                          width: cell.column.getSize(),
                          minWidth: cell.column.getSize(),
                          maxHeight: "60px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          position: cell.column.columnDef.meta?.isSticky
                            ? "sticky"
                            : undefined,
                          right: cell.column.columnDef.meta?.isSticky
                            ? 0
                            : undefined,
                          background: "white",
                          zIndex: 1,
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} className="text-center py-3">
                    Tidak ada data
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
